/**
 * What the `Records/*` list endpoints have in common: the query parameters they all take, and the
 * resolved location shapes their rows carry. Snake_case on the row shapes because they mirror the
 * wire verbatim; the parameter names are the frontend's own and are serialised at the call site.
 */

export type RecordsListFetchParams = {
    /**
     * The serialised period, straight off the `getSelectedPeriodByDate` getter — `from` and `to` as
     * plain `yyyy-MM-dd` days.
     *
     * It has to be that getter and not `getSelectedPeriod`: these endpoints filter on the day a
     * registration was booked on, so they take days. `getSelectedPeriod` sends instants, which the
     * server would have to read back into days through the directory's timezone — landing a day
     * early for any directory west of UTC, silently losing the period's last day.
     */
    period: string;
    locationIds: number[];
};

/**
 * Column id of the row-action chevron. The shared table renders this column itself, as a control
 * rather than a value, so a page declares the column but never a cell for it.
 */
export const RECORD_ACTIONS_COLUMN_ID = '_actions';

/** A named location in a resolved registration chain. */
export type RecordLocationRef = {
    id: number;
    name: string;
};

/**
 * A registration's actual target, which may sit deeper than the barn/section chain reaches — a pen
 * or a batch. Carried so the level is on the wire; the tables render down to Section only.
 */
export type RecordLocationTarget = RecordLocationRef & {
    type_id: number;
    level: number;
};

/**
 * The figures every registration list on a records page shares: a farm-local day, the server-computed
 * Monday it bins into, a head count and the two weights.
 *
 * This is all the weekly aggregation ever reads, so a new records page only has to satisfy this much
 * to reuse the chart, the totals and the week filter whole.
 */
export type RecordRow = {
    date: string;
    week_start: string;
    number: number;
    avg_weight: number | null;
    total_weight: number | null;
};

/**
 * What the shared records table can render off any row, whichever registration type a page shows:
 * the figures above, plus the registration handle and whichever location chains the page carries.
 *
 * Both chains are optional because a page need not have both, and either can fail to resolve: an
 * arrival carries a From chain only when it came from a transfer, and a move whose destination has
 * been deleted comes back with no To chain at all. A row type satisfying this can be handed to the
 * table; the columns decide what is read.
 */
export type RecordTableRow = RecordRow & {
    record_id: number;
    from_farm?: RecordLocationRef | null;
    from_barn?: RecordLocationRef | null;
    from_section?: RecordLocationRef | null;
    to_farm?: RecordLocationRef | null;
    to_barn?: RecordLocationRef | null;
    to_section?: RecordLocationRef | null;
    /** The registration's single location, on `SoldRow`/`SlaughterRow`/`StatusRow` only. */
    farm?: RecordLocationRef | null;
    barn?: RecordLocationRef | null;
    section?: RecordLocationRef | null;
    /** Integrator-only columns, on `SoldRow`/`SlaughterRow` only. */
    invoice?: string | null;
    customer?: string | null;
    /** Full name of the registering user, on the pigs record pages and approved feed deliveries. */
    created_by?: string | null;
};
